import React, {
  type ReactNode,
  type RefObject,
  forwardRef,
  useEffect,
  useRef,
  useState
} from "react";

import { NavigationIcons } from "@euka-mfes/bark";

import {
  Box,
  Drawer,
  Flex,
  Portal,
  Stack,
  type StackProps,
  type SystemStyleObject
} from "@chakra-ui/react";

import { IconButton } from "../IconButton";

export interface SidebarPropTypes {
  content: React.ReactNode;
  children?: ReactNode;
  sidebarWidth?: number | string;
  collapseAtWidth?: number;
  behaviour?: SidebarBehaviour.Overlay | SidebarBehaviour.Collapse;
  placement?: "start" | "end" | undefined;
  openTrigger?: RefObject<HTMLDivElement | null>;
  isSidebarOpen?: boolean;
  border?: string;
  sidebarBgColor?: SystemStyleObject["bg"];
  isMenuSideBar?: boolean;
  isCloseWithinContent?: boolean;
  onSidebarClose(event: React.MouseEvent<HTMLButtonElement>): void;
}

export enum SidebarBehaviour {
  Collapse = "COLLAPSE",
  Overlay = "OVERLAY"
}

// #region Handle container
const DrawerContainer = forwardRef<HTMLDivElement, StackProps>((props, ref) => {
  return (
    <Box ref={ref} p={0} {...props}>
      {props.children}
    </Box>
  );
});
// #endregion Handle container

const Sidebar = ({
  content,
  children,
  sidebarWidth = 300,
  collapseAtWidth = 800, // 768px breakpoint + 32px (2rem) padding
  behaviour = SidebarBehaviour.Overlay,
  placement = undefined,
  openTrigger,
  isSidebarOpen = false,
  onSidebarClose,
  border = "lines",
  sidebarBgColor = "transparent",
  isMenuSideBar = true,
  isCloseWithinContent = true
}: SidebarPropTypes) => {
  const sidebarWidthNum = typeof sidebarWidth === "string" ? 300 : sidebarWidth;
  const portalRef = useRef<HTMLDivElement | null>(null);
  const [activeBehaviour, setActiveBehaviour] = useState(behaviour);

  const getPersistentElements = (): Array<() => Element | null> => {
    if (typeof document === "undefined") {
      return [];
    }

    const elements = Array.from(
      document.querySelectorAll('[data-sidebar-persistent="true"]')
    );

    return elements.map(element => () => element);
  };

  // #region Resizing functionality
  const handleResize = () => {
    if (behaviour === SidebarBehaviour.Overlay) {
      return;
    }

    if (portalRef && portalRef.current) {
      const width = portalRef.current.offsetWidth;

      if (width + sidebarWidthNum * 0.5 < collapseAtWidth + sidebarWidthNum) {
        setActiveBehaviour(SidebarBehaviour.Overlay);
      } else if (
        width - sidebarWidthNum * 0.5 >
        collapseAtWidth + sidebarWidthNum
      ) {
        setActiveBehaviour(SidebarBehaviour.Collapse);
      }
    }

    if (openTrigger && openTrigger.current) {
      openTrigger.current.style.display =
        activeBehaviour === SidebarBehaviour.Collapse ? "none" : "flex";
    }
  };

  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [children]);
  // #endregion Resizing functionality

  // #region Shared content
  const SidebarContent = () => (
    <Stack
      w={sidebarWidth}
      data-testid="sidebar-content"
      minH="100vh"
      height={undefined}
      border="sm"
      borderColor={border}
      gap={0}
      bg={sidebarBgColor}
      overflow="visible"
      position={isMenuSideBar ? undefined : "relative"}
    >
      {isMenuSideBar && isCloseWithinContent && (
        <Box
          display="flex"
          justifyContent="flex-end"
          bgColor={sidebarBgColor as string}
          w="full"
          py={4}
          px={4}
          borderColor={border}
        >
          <IconButton
            icon={<NavigationIcons.Cross />}
            tooltipText="Close menu"
            tooltipProps={{
              disabled: true
            }}
            buttonProps={{
              onClick: onSidebarClose,
              border: "none",
              outline: "none",
              _hover: {
                bg: "transparent",
                border: "none",
                outline: "none"
              },
              _active: {
                bg: "transparent",
                border: "none",
                outline: "none"
              },
              _focus: {
                boxShadow: "none",
                outline: "none",
                border: "none"
              },
              _focusVisible: {
                boxShadow: "none",
                outline: "none",
                border: "none"
              },
              _after: {
                border: "none",
                outline: "none"
              },
              _before: {
                border: "none",
                outline: "none"
              }
            }}
          />
        </Box>
      )}
      {!isMenuSideBar && (
        <Box
          position="relative"
          top="7rem"
          alignSelf="flex-end"
          p={0}
          right="15px"
          zIndex={999}
          display="flex"
          justifyContent="flex-end"
        >
          <IconButton
            icon={<NavigationIcons.Cross />}
            size={40}
            buttonProps={{
              onClick: onSidebarClose,
              border: "none",
              outline: "none",
              _hover: {
                bg: "transparent",
                border: "none",
                outline: "none"
              },
              _active: {
                bg: "transparent",
                border: "none",
                outline: "none"
              },
              _focus: {
                boxShadow: "none",
                outline: "none",
                border: "none"
              },
              _focusVisible: {
                boxShadow: "none",
                outline: "none",
                border: "none"
              },
              _after: {
                border: "none",
                outline: "none"
              },
              _before: {
                border: "none",
                outline: "none"
              }
            }}
          />
        </Box>
      )}
      {content}
    </Stack>
  );
  // #endregion Shared content

  // #region Return component
  return (
    <Flex data-bark-component="Sidebar" h="full" w="full" gap={0}>
      {placement === "start" && (
        <Flex
          w="min-content"
          minH="100vh"
          display={
            activeBehaviour === SidebarBehaviour.Collapse ? "flex" : "none"
          }
        >
          <SidebarContent />
        </Flex>
      )}

      <Drawer.Root
        open={isSidebarOpen}
        placement={placement}
        closeOnInteractOutside={false}
        preventScroll
        persistentElements={getPersistentElements()}
      >
        <DrawerContainer ref={portalRef} children={children} />
        <Portal container={portalRef}>
          <Drawer.Backdrop pointerEvents="none" />
          <Drawer.Positioner
            pos="fixed"
            h={isMenuSideBar ? undefined : "100vh"}
            top={placement === "end" ? 0 : undefined}
            left={placement === "end" ? 0 : undefined}
            right={placement === "end" ? 0 : undefined}
            boxSize="full"
            w="full"
            maxW="full"
            overflow="auto"
            pointerEvents="none"
            zIndex="overlay"
            css={{
              "&::-webkit-scrollbar": {
                display: "none"
              },
              msOverflowStyle: "none",
              scrollbarWidth: "none"
            }}
          >
            <Drawer.Content
              bgColor="transparent"
              position="relative"
              shadow="none"
              w={sidebarWidth}
              maxW="100vw"
              overflow="auto"
              pointerEvents="auto"
              css={{
                "&::-webkit-scrollbar": {
                  display: "none"
                },
                msOverflowStyle: "none",
                scrollbarWidth: "none"
              }}
            >
              <Drawer.Body
                p={0}
                overflow="auto"
                position={isMenuSideBar ? "initial" : "relative"}
                css={{
                  "&::-webkit-scrollbar": {
                    display: "none"
                  },
                  msOverflowStyle: "none",
                  scrollbarWidth: "none"
                }}
              >
                <SidebarContent />
              </Drawer.Body>
            </Drawer.Content>
          </Drawer.Positioner>
        </Portal>
      </Drawer.Root>

      {placement === "end" && (
        <Stack
          w="min-content"
          minH="100vh"
          display={
            activeBehaviour === SidebarBehaviour.Collapse ? "flex" : "none"
          }
        >
          <SidebarContent />
        </Stack>
      )}
    </Flex>
  );
  // #endregion Return component
};

export default Sidebar;
